import { useEffect, useState, type ReactNode } from 'react';
import { Head, Link } from 'zudoku/components';
import {
  ArrowRightIcon,
  BookOpenIcon,
  CodeIcon,
  GlobeIcon,
  LifeBuoyIcon,
  MailIcon,
  SearchIcon,
} from 'zudoku/icons';

/**
 * The help centre's landing page (`/`).
 *
 * Kept out of the search index (`data-pagefind-ignore`): it only routes to the
 * real pages, so it would be noise in the results.
 */

/** Opens Zudoku's search dialog, which listens for ⌘K / Ctrl+K on `window`. */
const openSearch = () => {
  window.dispatchEvent(
    new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, metaKey: true, bubbles: true }),
  );
};

const POPULAR = [
  { label: 'Mettre en route mon site', to: '/site-de-reservation/demarrer' },
  { label: 'Brancher mon nom de domaine', to: '/site-de-reservation/domaine-personnalise' },
  { label: 'Accepter une demande', to: '/site-de-reservation/quand-un-voyageur-reserve' },
  { label: 'Dépannage', to: '/site-de-reservation/depannage' },
];

const BOOKING_LINKS = [
  { label: 'Mise en route pas à pas', to: '/site-de-reservation/demarrer' },
  { label: 'Logements et photos', to: '/site-de-reservation/logements-et-photos' },
  { label: 'Tarifs et disponibilités', to: '/site-de-reservation/tarifs-et-disponibilites' },
  { label: 'Paiements et encaissement', to: '/site-de-reservation/paiements-et-encaissement' },
  { label: 'Quand un voyageur réserve', to: '/site-de-reservation/quand-un-voyageur-reserve' },
  { label: 'Votre propre nom de domaine', to: '/site-de-reservation/domaine-personnalise' },
];

const STEPS = [
  {
    title: 'Vos logements',
    text: 'Choisissez les logements à proposer. Leurs photos viennent de leur fiche dans Noé.',
  },
  {
    title: 'Votre image',
    text: 'Votre nom, votre logo, vos couleurs, et les pages de votre site.',
  },
  {
    title: 'Vos prix et paiements',
    text: 'Vos tarifs directs, vos conditions d’annulation et votre compte de paiement.',
  },
  {
    title: 'En ligne',
    text: 'Langue, référencement, adresse du site… puis un aperçu et la publication.',
  },
];

const Card = ({
  to,
  href,
  icon,
  title,
  children,
  cta,
}: {
  to?: string;
  href?: string;
  icon: ReactNode;
  title: string;
  children: ReactNode;
  cta: string;
}) => {
  const body = (
    <>
      <span className="noe-card-icon">{icon}</span>
      <h3 className="noe-heading text-lg font-bold mt-5 mb-2">{title}</h3>
      <p className="text-[15px] leading-relaxed text-muted-foreground m-0 grow">{children}</p>
      <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold noe-link-color">
        {cta}
        <ArrowRightIcon size={15} strokeWidth={2.25} aria-hidden="true" className="noe-arrow" />
      </span>
    </>
  );
  const className = 'noe-card group flex flex-col p-7 no-underline';
  return to ? (
    <Link to={to} className={className}>
      {body}
    </Link>
  ) : (
    <a href={href} className={className}>
      {body}
    </a>
  );
};

export const HomePage = () => {
  const [shortcut, setShortcut] = useState('Ctrl K');
  useEffect(() => {
    if (/Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)) setShortcut('⌘ K');
  }, []);

  return (
    <div className="noe-home" data-pagefind-ignore="all">
      <Head>
        <title>Tout pour réussir avec Noé</title>
        <meta
          name="description"
          content="Guides pas à pas pour gérer votre conciergerie avec Noé et lancer votre site de réservation directe."
        />
      </Head>

      {/* Hero */}
      <section className="noe-hero">
        <div className="noe-hero-inner">
          <span className="noe-eyebrow">Centre d’aide</span>
          <h1 className="noe-heading noe-hero-title">
            {/* The space lives inside the brand span: Google trims it from the translated text. */}
            Tout pour réussir avec<span className="text-primary notranslate" translate="no">{' '}Noé</span>
          </h1>
          <p className="noe-hero-lead">
            Des guides clairs, pas à pas, pour gérer votre conciergerie au quotidien et lancer
            votre site de réservation directe.
          </p>

          <button type="button" onClick={openSearch} className="noe-search" aria-label="Rechercher dans l’aide">
            <SearchIcon size={20} strokeWidth={2} aria-hidden="true" className="shrink-0 text-muted-foreground" />
            <span className="grow text-left truncate">Rechercher un guide, une question…</span>
            <kbd className="noe-kbd">{shortcut}</kbd>
          </button>

          <div className="noe-popular">
            <span className="text-muted-foreground">Populaire :</span>
            {POPULAR.map((p) => (
              <Link key={p.to} to={p.to} className="noe-chip">
                {p.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div className="noe-home-body">
        {/* Featured: the direct booking website */}
        <section className="noe-feature">
          <div className="noe-feature-text">
            <div className="flex items-center gap-3">
              <span className="noe-card-icon noe-card-icon--solid">
                <GlobeIcon size={22} strokeWidth={2} aria-hidden="true" />
              </span>
              <span className="noe-badge">Nouveau</span>
            </div>
            <h2 className="noe-heading text-2xl md:text-3xl font-bold mt-5 mb-3">Site de réservation</h2>
            <p className="text-base leading-relaxed text-muted-foreground m-0">
              Votre propre site pour recevoir des réservations en direct, sans passer par les
              plateformes. Tout se règle depuis Noé, et chaque réservation arrive dans votre
              calendrier comme les autres.
            </p>
            <Link to="/site-de-reservation" className="noe-btn-primary mt-7">
              Découvrir le site de réservation
              <ArrowRightIcon size={16} strokeWidth={2.25} aria-hidden="true" />
            </Link>
          </div>
          <ul className="noe-feature-links">
            {BOOKING_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="noe-feature-link group">
                  <span>{l.label}</span>
                  <ArrowRightIcon size={15} aria-hidden="true" className="noe-arrow shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        </section>

        {/* The other doors */}
        <section className="noe-cards" aria-label="Rubriques">
          <Card
            to="/guide"
            icon={<BookOpenIcon size={22} strokeWidth={2} aria-hidden="true" />}
            title="Guide d’utilisation"
            cta="Parcourir le guide"
          >
            Calendrier, réservations, messagerie, ménage, paiements, propriétaires… chaque module
            de Noé expliqué simplement.
          </Card>
          <Card
            to="/developpeurs"
            icon={<CodeIcon size={22} strokeWidth={2} aria-hidden="true" />}
            title="Développeurs"
            cta="Voir la documentation API"
          >
            Connectez vos outils à Noé grâce à l’API publique : guides techniques, webhooks et
            référence complète.
          </Card>
          <Card
            href="mailto:support@wearenoe.fr"
            icon={<LifeBuoyIcon size={22} strokeWidth={2} aria-hidden="true" />}
            title="Besoin d’aide ?"
            cta="Écrire au support"
          >
            Vous ne trouvez pas votre réponse ? Notre équipe vous répond par e-mail à
            support@wearenoe.fr.
          </Card>
        </section>

        {/* Set-up in four moves */}
        <section className="noe-steps">
          <div className="max-w-2xl">
            <h2 className="noe-heading text-2xl font-bold m-0">Votre site en ligne, étape par étape</h2>
            <p className="text-muted-foreground mt-2 mb-0">
              Un assistant vous accompagne, du choix de vos logements jusqu’à la publication.
            </p>
          </div>
          <ol className="noe-steps-list">
            {STEPS.map((s, i) => (
              <li key={s.title} className="noe-step">
                <span className="noe-step-num">{i + 1}</span>
                <h3 className="noe-heading text-base font-bold mt-4 mb-1.5">{s.title}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground m-0">{s.text}</p>
              </li>
            ))}
          </ol>
          <Link to="/site-de-reservation/demarrer" className="noe-link-color inline-flex items-center gap-1.5 text-sm font-semibold mt-2">
            Suivre le guide de mise en route
            <ArrowRightIcon size={15} strokeWidth={2.25} aria-hidden="true" />
          </Link>
        </section>

        {/* Contact band */}
        <section className="noe-contact">
          <div>
            <h2 className="noe-heading text-xl md:text-2xl font-bold m-0">Une question ? Parlons-en.</h2>
            <p className="m-0 mt-2 opacity-80">
              Décrivez-nous votre situation : nous vous répondons avec la marche à suivre.
            </p>
          </div>
          <a href="mailto:support@wearenoe.fr" className="noe-btn-light">
            <MailIcon size={16} aria-hidden="true" />
            support@wearenoe.fr
          </a>
        </section>
      </div>
    </div>
  );
};
